import { format, parse } from 'date-fns'
import { ptBR } from 'date-fns/locale'
export const formatDateToString = (date: Date) => {
  return format(date, 'dd/MM/yyyy')
}

export function formatDateTimeWithTimezone(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (isNaN(date.getTime())) return '-'

  const datePart = format(date, 'dd/MM/yyyy')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  const offsetMinutes = date.getTimezoneOffset()
  const sign = offsetMinutes <= 0 ? '+' : '-'
  const absHours = Math.floor(Math.abs(offsetMinutes) / 60)
  const absMinutes = Math.abs(offsetMinutes) % 60
  const tz =
    absMinutes === 0
      ? `GMT${sign}${absHours}`
      : `GMT${sign}${absHours}:${String(absMinutes).padStart(2, '0')}`

  return `${datePart}, às ${hours}:${minutes}, ${tz}`
}

// Fuso padrão do produto (Brasília) — mesmo fallback já repetido em ~10 telas
// (`timezone || '-03:00'`).
export const DEFAULT_UTC_OFFSET = '-03:00'

// Offset ISO como o backend persiste no plantão: `-03:00`, `-0300` ou `-03`
// (tabela `timezones` em `src/hooks/useCommon.ts`). NÃO é nome IANA.
const UTC_OFFSET_PATTERN = /^([+-])(\d{2})(?::?(\d{2}))?$/

const parseUtcOffsetToMinutes = (offset: string): number => {
  const match = UTC_OFFSET_PATTERN.exec(offset.trim())
  // Valor fora do formato de offset (nulo, vazio ou um nome IANA vindo de um
  // caller antigo): assume o fuso padrão em vez de produzir NaN na data.
  //
  // Para nome IANA isso é uma perda de precisão CONSCIENTE: resolver
  // `America/Sao_Paulo` pela base de fusos daria horário de verão, um offset
  // fixo não. O Brasil não adota DST desde 2019, então na prática o resultado
  // é o mesmo — e é infinitamente melhor que o `RangeError` que o `Intl`
  // lança ao receber um offset ISO na opção `timeZone`.
  if (!match) return parseUtcOffsetToMinutes(DEFAULT_UTC_OFFSET)

  const [, sign, hours, minutes] = match
  const total = Number(hours) * 60 + Number(minutes ?? 0)
  return sign === '-' ? -total : total
}

const padTwo = (value: number): string => String(value).padStart(2, '0')

// Desloca o instante e devolve uma `Date` cujos campos UTC já são a hora local
// do plantão: assim o resultado depende só do offset recebido (`getHours()`
// traria a hora da máquina do usuário). `null` quando o ISO é inválido.
//
// Este é o ÚNICO lugar do frontend que converte instante + offset de plantão —
// os formatadores públicos abaixo apenas escolhem quais campos imprimir.
const shiftIsoByOffsetMinutes = (iso: string, offsetMinutes: number): Date | null => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Date(date.getTime() + offsetMinutes * 60_000)
}

// `UTC-3` para offsets em hora cheia e `UTC-3:30` quando há minutos — o rótulo
// do mockup é sem o zero à esquerda (`UTC-3`, não `UTC-03:00`).
export const formatUtcOffsetLabel = (offsetMinutes: number): string => {
  const sign = offsetMinutes < 0 ? '-' : '+'
  const absolute = Math.abs(offsetMinutes)
  const hours = Math.floor(absolute / 60)
  const minutes = absolute % 60
  return minutes === 0
    ? `UTC${sign}${hours}`
    : `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * Formata um instante UTC no fuso de um plantão, com o fuso explícito no texto:
 *
 * ```
 * formatDateTimeWithOffset('2025-05-14T13:32:00.000Z', '-03:00')
 * // '14/05/2025 às 10:32 UTC-3'
 * ```
 *
 * Existe separado de `formatDateTimeWithTimezone` de propósito: aquele imprime
 * `GMT` e usa o offset do NAVEGADOR (o que os 3 callers dele esperam), enquanto
 * aqui o fuso é um dado do plantão. Offsets com minutos (`-03:30`) são
 * suportados. Retorna `'-'` quando o ISO é inválido.
 */
export function formatDateTimeWithOffset(iso: string, offset: string): string {
  const offsetMinutes = parseUtcOffsetToMinutes(offset)
  const shifted = shiftIsoByOffsetMinutes(iso, offsetMinutes)
  if (!shifted) return '-'

  const day = padTwo(shifted.getUTCDate())
  const month = padTwo(shifted.getUTCMonth() + 1)
  const year = shifted.getUTCFullYear()
  const hours = padTwo(shifted.getUTCHours())
  const minutes = padTwo(shifted.getUTCMinutes())

  return `${day}/${month}/${year} às ${hours}:${minutes} ${formatUtcOffsetLabel(offsetMinutes)}`
}

/**
 * `dd/MM` do instante UTC lido no fuso do plantão:
 *
 * ```
 * formatDayMonthInOffset('2026-05-13T01:00:00.000Z', '-03:00') // '12/05'
 * ```
 *
 * Existe para o grid/detalhe do pós-plantão, onde a coluna mostra só dia e mês.
 * Retorna `null` no ISO inválido — o caller escolhe o placeholder visual.
 *
 * Aceita offset ISO (`-03:00`, `-0300`, `-03`) e tolera nome IANA caindo no fuso
 * padrão de Brasília — ver `parseUtcOffsetToMinutes`. NÃO usa a opção `timeZone`
 * do `Intl` de propósito: ela lança `RangeError` para offsets ISO, que é o
 * formato em que o backend persiste `stations.timezone`.
 */
export const formatDayMonthInOffset = (iso: string, offset: string): string | null => {
  const shifted = shiftIsoByOffsetMinutes(iso, parseUtcOffsetToMinutes(offset))
  if (!shifted) return null

  return `${padTwo(shifted.getUTCDate())}/${padTwo(shifted.getUTCMonth() + 1)}`
}

/**
 * `HH:mm` do instante UTC lido no fuso do plantão:
 *
 * ```
 * formatHourMinuteInOffset('2026-05-13T01:00:00.000Z', '-03:00') // '22:00'
 * ```
 *
 * Mesmas garantias de formato de offset e de não usar `Intl` que
 * `formatDayMonthInOffset`. Retorna `null` no ISO inválido.
 */
export const formatHourMinuteInOffset = (iso: string, offset: string): string | null => {
  const shifted = shiftIsoByOffsetMinutes(iso, parseUtcOffsetToMinutes(offset))
  if (!shifted) return null

  return `${padTwo(shifted.getUTCHours())}:${padTwo(shifted.getUTCMinutes())}`
}

/** Dia da semana abreviado em pt-BR, minúsculo e sem ponto; índice = `getUTCDay()`. */
const WEEKDAY_SHORT_PT_BR = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const

/**
 * Dia da semana abreviado do instante lido no fuso recebido:
 *
 * ```
 * formatWeekdayShortInOffset('2026-09-28T14:00:00.000Z', '-03:00') // 'seg'
 * ```
 *
 * Mesmas garantias de `formatDayMonthInOffset`. Retorna `null` no ISO inválido.
 */
export const formatWeekdayShortInOffset = (iso: string, offset: string): string | null => {
  const shifted = shiftIsoByOffsetMinutes(iso, parseUtcOffsetToMinutes(offset))
  if (!shifted) return null

  return WEEKDAY_SHORT_PT_BR[shifted.getUTCDay()]
}

/**
 * Diferença em dias CIVIS (no fuso recebido) de `iso` para `referenceIso`:
 * `0` = mesmo dia, `1` = dia seguinte, negativo = dia anterior. Não é a duração
 * dividida por 24 h: 23:59 → 00:01 do dia seguinte vale `1`. `null` se algum ISO
 * for inválido.
 */
export const civilDayDiffInOffset = (
  iso: string,
  referenceIso: string,
  offset: string
): number | null => {
  const offsetMinutes = parseUtcOffsetToMinutes(offset)
  const shifted = shiftIsoByOffsetMinutes(iso, offsetMinutes)
  const reference = shiftIsoByOffsetMinutes(referenceIso, offsetMinutes)
  if (!shifted || !reference) return null

  const dayIndex = (d: Date) => Math.floor(d.getTime() / 86_400_000)
  return dayIndex(shifted) - dayIndex(reference)
}

/**
 * `dd/MM/yyyy` do instante UTC lido no fuso recebido (padrão Brasília):
 *
 * ```
 * formatDateInOffset('2026-08-05T02:30:00.000Z') // '04/08/2026'
 * ```
 *
 * Mesmas garantias de formato de offset e de não usar `Intl` que
 * `formatDayMonthInOffset`. Retorna `null` no ISO inválido.
 */
export const formatDateInOffset = (
  iso: string,
  offset: string = DEFAULT_UTC_OFFSET
): string | null => {
  const shifted = shiftIsoByOffsetMinutes(iso, parseUtcOffsetToMinutes(offset))
  if (!shifted) return null

  return `${padTwo(shifted.getUTCDate())}/${padTwo(shifted.getUTCMonth() + 1)}/${shifted.getUTCFullYear()}`
}

/**
 * `HH:mm` com o fuso explícito no texto, no offset recebido (padrão Brasília):
 *
 * ```
 * formatHourMinuteWithZoneInOffset('2026-08-05T02:30:00.000Z') // '23:30 UTC-3'
 * ```
 *
 * É a metade "hora" de `formatDateTimeWithOffset`, para telas que exibem data e
 * hora em elementos separados. Retorna `null` no ISO inválido.
 */
export const formatHourMinuteWithZoneInOffset = (
  iso: string,
  offset: string = DEFAULT_UTC_OFFSET
): string | null => {
  const offsetMinutes = parseUtcOffsetToMinutes(offset)
  const shifted = shiftIsoByOffsetMinutes(iso, offsetMinutes)
  if (!shifted) return null

  return `${padTwo(shifted.getUTCHours())}:${padTwo(shifted.getUTCMinutes())} ${formatUtcOffsetLabel(offsetMinutes)}`
}

/**
 * Offset normalizado para `±HH:mm`, do jeito que entra num ISO 8601:
 *
 * ```
 * normalizeUtcOffset('-03')   // '-03:00'
 * normalizeUtcOffset('bobo')  // '-03:00'  (fuso padrão do produto)
 * ```
 */
export const normalizeUtcOffset = (offset: string = DEFAULT_UTC_OFFSET): string => {
  const total = parseUtcOffsetToMinutes(offset)
  const sign = total < 0 ? '-' : '+'
  const absolute = Math.abs(total)
  return `${sign}${padTwo(Math.floor(absolute / 60))}:${padTwo(absolute % 60)}`
}

/**
 * O INVERSO de `formatHourMinuteInOffset`: um `HH:mm` digitado pelo usuário vira
 * um instante ISO 8601 no fuso do plantão, mantendo o DIA que a data base tem
 * naquele fuso.
 *
 * ```
 * buildIsoAtOffset('2026-09-09T11:00:00.000Z', '08:00', '-03:00')
 * // '2026-09-09T08:00:00-03:00'
 * ```
 *
 * Existe porque `new Date(...).setHours()` + `toISOString()` — o caminho antigo
 * do modal "Enviar registro" — resolve o `HH:mm` no fuso do NAVEGADOR: o mesmo
 * 08:00 saía como `11:00Z` no Brasil e `08:00Z` num navegador em UTC, e o
 * backend gravava horários diferentes para o mesmo plantão. Aqui o offset é um
 * dado do plantão, e a string carrega o offset explícito para o backend não ter
 * de adivinhar. `null` quando o horário não é `HH:mm` ou a base é inválida.
 */
export const buildIsoAtOffset = (
  base: string | null | undefined,
  time: string,
  offset: string = DEFAULT_UTC_OFFSET
): string | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec((time ?? '').trim())
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null

  const normalized = normalizeUtcOffset(offset)
  // Sem base, o dia é "hoje" no fuso do plantão — nunca no da máquina.
  const reference = base ?? new Date().toISOString()
  const shifted = shiftIsoByOffsetMinutes(reference, parseUtcOffsetToMinutes(offset))
  if (!shifted) return null

  const day = `${shifted.getUTCFullYear()}-${padTwo(shifted.getUTCMonth() + 1)}-${padTwo(shifted.getUTCDate())}`
  return `${day}T${padTwo(hours)}:${padTwo(minutes)}:00${normalized}`
}

/**
 * `true` quando o instante já passou:
 *
 * ```
 * hasInstantPassed('2020-01-01T00:00:00.000Z') // true
 * hasInstantPassed('2999-01-01T00:00:00.000Z') // false
 * ```
 *
 * NÃO usa a maquinaria de offset deste arquivo de propósito: `startHour` /
 * `endHour` do plantão são instantes absolutos, e comparar dois instantes é
 * independente de fuso — o offset do plantão só importa para EXIBIR a hora de
 * parede. Ausente ou inválido devolve `false`: sem instante não há como afirmar
 * que algo começou, e o caller trata isso como "ainda não".
 */
export const hasInstantPassed = (iso?: string | null): boolean => {
  if (!iso) return false

  const instant = new Date(iso).getTime()
  if (isNaN(instant)) return false

  return instant <= Date.now()
}

export const formatStringToDate = (dateStr: string): Date => {
  return parse(dateStr, 'dd/MM/yyyy', new Date())
}

export function parseDateStringToISO(dateString?: string): string | undefined {
  if (!dateString) return undefined
  // Verifica se o formato é dd/MM/yyyy
  const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/
  const match = dateString.match(datePattern)

  if (!match) {
    throw new Error('Formato de data inválido. Use o formato dd/MM/yyyy.')
  }

  // Extrai o dia, mês e ano
  const [, day, month, year] = match

  // Retorna no formato ISO
  return `${year}-${month}-${day}`
}

export const daysWeekObj = {
  '0': 'Domingo',
  '1': 'Segunda',
  '2': 'Terça',
  '3': 'Quarta',
  '4': 'Quinta',
  '5': 'Sexta',
  '6': 'Sábado'
}

export const weekDays = [
  { title: 'Domingo', value: 0 },
  { title: 'Segunda', value: 1 },
  { title: 'Terça', value: 2 },
  { title: 'Quarta', value: 3 },
  { title: 'Quinta', value: 4 },
  { title: 'Sexta', value: 5 },
  { title: 'Sábado', value: 6 }
]

export const toDateOnly = (d: Date | string): string => {
  if (typeof d === 'string') return d.slice(0, 10)
  return format(d, 'yyyy-MM-dd')
}

const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?/
// Dia/mês de 1 ou 2 dígitos: digitação manual não garante o zero à esquerda (`5/7/2026`
// é o mesmo valor que `05/07/2026`, e a RN-03 compara por valor).
const BR_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/

/**
 * RN-03 (#2109/B1) — normaliza qualquer uma das três formas em que uma data de folha de
 * frequência chega (`YYYY-MM-DD` do pré-preenchimento, `YYYY-MM-DD HH:MM:SS` do date picker
 * jSuites, `DD/MM/YYYY` de digitação/colagem) para `YYYY-MM-DD`, para que a comparação da
 * RN-03 seja por valor e não por forma de string. Entrada vazia devolve `''`; qualquer forma
 * que não bata com nenhum padrão volta trimada como veio (RN-08 — preservar texto não
 * parseável em vez de descartar).
 */
export const toCanonicalDateOnly = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  const trimmed = String(value).trim()
  if (trimmed === '') return ''

  if (ISO_DATE_ONLY_PATTERN.test(trimmed)) return trimmed
  if (ISO_DATE_TIME_PATTERN.test(trimmed)) return trimmed.slice(0, 10)

  const brMatch = BR_DATE_PATTERN.exec(trimmed)
  if (brMatch) {
    const [, day, month, year] = brMatch
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  return trimmed
}

export const parseLocalDate = (val: Date | string | null | undefined): Date | null => {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const parsed = new Date(val)
  return isNaN(parsed.getTime()) ? null : parsed
}

/*
 * Formatadores de vigência de política (`startDate` / `endDate` / `createdAt`).
 *
 * Os três abaixo saíram de cópias locais espalhadas por `src/modules/company/Policy/`
 * e pelas abas de política do `EditScheduleModal`. Eles produzem saídas DIFERENTES
 * para a mesma entrada — isso é preservado de propósito: unificar a saída é decisão
 * de produto, não de refactor. Ver a divergência documentada abaixo.
 *
 * | Entrada `'2026-07-15T00:00:00.000Z'` | Saída                        |
 * | ----------------------------------- | ---------------------------- |
 * | `formatDateTimeBR`                  | `14/07/2026, 21:00:00`       |
 * | `formatIsoDateOnlyBR`               | `15/07/2026`                 |
 * | `formatDateTimeAtBR`                | `14/07/2026 às 21:00`        |
 *
 * A diferença de DIA não é bug de formatação: o backend manda a vigência como
 * data de calendário serializada à meia-noite UTC, e os dois formatadores que
 * passam por `new Date()` a deslocam para o fuso do host (UTC-3 ⇒ dia anterior).
 * `formatIsoDateOnlyBR` não desloca porque fatia a string antes de parsear.
 * Decidir o que o campo significa (data de calendário ou instante) é o que fecha
 * essa divergência.
 */

/**
 * Vigência com data e hora no locale pt-BR (`dd/MM/yyyy, HH:mm:ss`), deslocada
 * para o fuso do host.
 *
 * Ausente devolve `'Indeterminado'` (vigência em aberto); inválida devolve `'-'`.
 */
export const formatDateTimeBR = (value: string | null | undefined): string => {
  if (!value) return 'Indeterminado'
  const date = new Date(value)
  return isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR')
}

/**
 * Vigência só com a data (`dd/MM/yyyy`), lida direto da string ISO.
 *
 * NÃO passa por `new Date()` de propósito — fatiar a string preserva a data de
 * calendário que o backend enviou, sem deslocamento de fuso.
 *
 * Ausente devolve `'Indeterminado'`. Entrada malformada não é validada: a string
 * é fatiada como vier, então `'xx'` produz `'undefined/undefined/xx'`.
 */
export const formatIsoDateOnlyBR = (iso: string | null | undefined): string => {
  if (!iso) return 'Indeterminado'
  const dateOnly = iso.includes('T') ? iso.split('T')[0] : iso
  const [year, month, day] = dateOnly.split('-')
  return `${day}/${month}/${year}`
}

/**
 * Data e hora por extenso (`dd/MM/yyyy 'às' HH:mm`), deslocada para o fuso do host.
 *
 * ⚠️ Não tem guarda: `date-fns/format` lança `RangeError: Invalid time value`
 * para entrada ausente ou inválida. É o comportamento original dos dois call
 * sites (`authorized.tab` / `blocked.tab`), onde o campo é sempre preenchido —
 * preservado aqui para o refactor não mudar comportamento. Caller novo com
 * campo opcional deve guardar antes de chamar.
 */
export const formatDateTimeAtBR = (dateStr: string): string => {
  return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
}
